import React, { useState, useContext, useRef } from 'react';
import { Tabs, TabPanel, Modal } from 'anf-core-react';
import ProductInfo from './ProductInfo';
import SizeGuideImages from './SizeGuideImages';
import SizePanel from './SizePanel';
import BreakpointContext from '../../contexts/BreakpointContext';
import ProductPageContext from '../../contexts/ProductPageContext';
import useHasGoldenStateAnalytics from '../../helpers/hasGoldenStateAnalytics';
import analyticsTriggers from '../../analytics/analyticsTriggers';
import HowToMeasurePanel from './HowToMeasurePanel';

export default function SizeGuideModal({
  openSizeGuide, setSizeGuideOpen,
}) {
  const [tabChange, setTabChange] = useState('size');
  const defaultOnChange = (name) => {
    setTabChange(name);
  };
  const breakpoints = useContext(BreakpointContext);
  const productPageState = useContext(ProductPageContext);
  const hasGoldenStateAnalytics = useHasGoldenStateAnalytics();

  const {
    tmntData,
    content,
    collectionData,
  } = productPageState;

  const howToMeasureContent = !!content?.legacyPDPESpots?.pdp_sizeGuideHowToMeasure?.content.trim();
  const {
    brand, imageSet, productName, garmentMeasurements, kicId, hasGarmentMeasurements,
  } = collectionData.activeProduct;

  const [visibleTabID, setVisibleTabID] = useState('size');
  const tabPanelIDs = ['size', 'measure'];
  const refs = {
    size: useRef(null),
    measure: useRef(null),
  };

  const handleKeyDown = (e) => {
    const directionalArrowCodes = ['ArrowLeft', 'ArrowRight'];

    if (directionalArrowCodes.includes(e.key)) {
      e.preventDefault();

      const currentIndex = tabPanelIDs.indexOf(visibleTabID);

      const deltasByCode = {
        ArrowLeft: -1,
        ArrowRight: 1,
      };

      const indexOfTabToShow = (tabPanelIDs.length + currentIndex + deltasByCode[e.key])
        % tabPanelIDs.length;

      setVisibleTabID(tabPanelIDs[indexOfTabToShow]);
      setTabChange(tabPanelIDs[indexOfTabToShow]);

      if (refs[tabPanelIDs[indexOfTabToShow]].current) {
        refs[tabPanelIDs[indexOfTabToShow]].current.focus();
      }
    }
  };

  const handleOnClick = (tabName) => {
    setTabChange(tabName);
    analyticsTriggers.universalClick('open', 'size guide - how to measure', hasGoldenStateAnalytics, productPageState);
  };

  return openSizeGuide && (
    <Modal
      isFullBleed
      isOpen={openSizeGuide}
      isFullscreen={!breakpoints.xsmall}
      variant="headerless"
      id="size-guide-modal"
      onClose={() => setSizeGuideOpen(false)}
    >
      <div className="size-guide-content scope-1892">
        <div className="size-guide-images-wrapper-mfe">
          <SizeGuideImages imageSet={imageSet} />
          {garmentMeasurements && (
            <ProductInfo
              brand={brand}
              tmntData={tmntData}
              productName={productName}
              garmentMeasurements={garmentMeasurements}
              hasGarmentMeasurements={hasGarmentMeasurements}
            />
          )}
        </div>
        <Tabs
          selectedTabId={tabChange}
          onChange={defaultOnChange}
          labelText=""
          controls={{
            onKeyDown: handleKeyDown,
            tabs: howToMeasureContent
              ? [
                {
                  ariaControls: 'size-tabpanel',
                  id: 'size',
                  isSelected: tabChange === 'size',
                  label: tmntData?.sizeGuideSizeFit?.value,
                  onClick: () => { setTabChange('size'); },
                  ref: refs.size,
                  subLabel: undefined,
                },
                {
                  ariaControls: 'measure-tabpanel',
                  id: 'measure',
                  isSelected: tabChange === 'measure',
                  label: tmntData?.sizeGuideHowMeasure?.value,
                  onClick: () => { handleOnClick('measure'); },
                  ref: refs.measure,
                  subLabel: undefined,
                },
              ]
              : [],
          }}
        >
          <TabPanel
            id="planning-tabpanel"
            isVisible={tabChange === 'size'}
            labelledBy="planning-tab"
          >
            <SizePanel
              sizeChartName={collectionData.sizeChartType}
              sizeChartSeq={collectionData.sizeChartSeq}
              tmntData={tmntData}
              eSpotContentData={content}
              kicId={kicId}
              siteRegion={collectionData.siteRegion}
            />
          </TabPanel>
          {howToMeasureContent && (
            <TabPanel
              isVisible={tabChange === 'measure'}
              id="feedback-tabpanel"
              labelledBy="feedback-tab"
            >
              <HowToMeasurePanel
                collectionData={collectionData}
                content={content}
              />
            </TabPanel>
          )}
        </Tabs>
      </div>
    </Modal>
  );
}
